import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Window,
  WindowHeader,
  WindowContent,
  Frame,
  ScrollView,
  Button,
  Select,
  TextInput,
  Checkbox,
  GroupBox
} from 'react95';
import classNames from 'classnames';
import { get, isEmpty } from 'lodash-es';
import { setModalType } from '../../actions/taskDetailActions';
import { userSelector } from '../../selectors/authSelectors';
import { TASK_DETAIL_ACTIONS, USER_ESC_TYPES } from '../../global/Environment';
import './UserEscModal.scss';
import { useGetEscalationErrorsQuery, useUpdateUserEscMutation } from '../../api/opsSystemApi';

const UserEscModal = ({ modalType, task, user, setModalType }) => {
  const [userEscType, setUserEscType] = useState('standard');
  const [userEscErrors, setUserEscErrors] = useState([]);
  const [footerText, setFooterText] = useState(
    'User escalation is only available for tasks in the following stages: Ops Review, Final Review, Tax Expert Escalations, Filed with IRS, Accepted by IRS, Eng Escalation'
  );
  const isCurrentUserEsc = ['user escalation', 'DO NOT FILE'].includes(get(task, 'stage'));

  const [updateUserEsc] = useUpdateUserEscMutation();

  const { data: escalationErrors = [], isLoading: areEscalationErrorsLoading } = useGetEscalationErrorsQuery();

  useEffect(() => {
    if (isCurrentUserEsc) {
      if (get(task, 'user_facing_error_type')) {
        setUserEscType(get(task, 'user_facing_error_type'));
      }

      if (!isEmpty(get(task, 'user_facing_errors'))) {
        setUserEscErrors(get(task, 'user_facing_errors'));
      }
    }
  }, [task, isCurrentUserEsc]);

  const selectErrorTemplate = (errorTemplate, idx) => {
    const updatedErrors = userEscErrors.map((current, i) => {
      if (idx === i) {
        return {
          slug: errorTemplate.slug,
          header: errorTemplate.header,
          body: errorTemplate.body,
          isBlocking: errorTemplate.isBlocking || false,
          isPostSubmit: errorTemplate.isPostSubmit || false,
          link: errorTemplate.link,
          linkText: errorTemplate.linkText
        };
      }
      return current;
    });
    setUserEscErrors(updatedErrors);
  };

  const handleSubmit = () => {
    if (userEscErrors.some((error) => error.isPostSubmit) && userEscErrors.some((error) => !error.isPostSubmit)) {
      setFooterText('Error: Cannot combine post-submit and standard filing flow errors');
    } else {
      updateUserEsc({
        taskId: task.id,
        origin: user.email,
        userFacingErrors: userEscErrors,
        escalationType: userEscType
      });
      setModalType(null);
    }
  };

  if (modalType !== TASK_DETAIL_ACTIONS.USER_ESCALATION || isEmpty(task)) {
    return null;
  }

  return (
    <Window className='user-esc-modal'>
      <WindowHeader className='user-esc-modal-header'>
        <span>User Escalation</span>
        <Button primary square size='sm' onClick={() => setModalType(null)}>
          X
        </Button>
      </WindowHeader>

      <WindowContent className='user-esc-modal-body'>
        <ScrollView className='user-esc-modal-body-scroll'>
          <div className='user-esc-modal-body-options'>
            <GroupBox label='Which screen to show' className='user-esc-modal-body-options-type'>
              <Select
                width={'100%'}
                menuMaxHeight={300}
                options={USER_ESC_TYPES}
                value={userEscType}
                onChange={(opt) => setUserEscType(opt.value)}
                disabled={isCurrentUserEsc}
              />
            </GroupBox>
            <Button
              primary
              className='user-esc-modal-body-options-add'
              variant='flat'
              size='md'
              disabled={isCurrentUserEsc}
              onClick={() =>
                setUserEscErrors([...userEscErrors, { header: '', body: '', isBlocking: false, isPostSubmit: false }])
              }
            >
              Add new error
            </Button>
          </div>

          <GroupBox label='User facing errors'>
            <div className='user-esc-modal-body-form'>
              {userEscErrors.map((error, i) => (
                <div key={i} className='user-esc-modal-body-form-error'>
                  <div className='user-esc-modal-body-form-error-label'>Error {i + 1}</div>
                  <div className='user-esc-modal-body-form-error-template'>
                    <div>Select error type (optional):</div>
                    <Select
                      className='user-esc-modal-body-form-error-template-select'
                      menuMaxHeight={300}
                      options={escalationErrors.map((escErrorTemplate) => ({
                        ...escErrorTemplate,
                        label: escErrorTemplate.header,
                        value: escErrorTemplate.slug
                      }))}
                      value={userEscErrors[i].slug || ''}
                      onChange={(opt) => selectErrorTemplate(opt, i)}
                      disabled={isCurrentUserEsc}
                    />
                    <Button
                      size='sm'
                      onClick={() => {
                        const updatedErrors = userEscErrors.filter((x, idx) => idx !== i);
                        setUserEscErrors(updatedErrors);
                      }}
                      disabled={isCurrentUserEsc}
                    >
                      Remove
                    </Button>
                  </div>
                  <TextInput
                    placeholder='Error header'
                    value={error.header || ''}
                    onChange={(e) => {
                      const updatedErrors = userEscErrors.map((current, idx) => {
                        if (idx === i) {
                          return {
                            ...current,
                            header: e.target.value
                          };
                        }
                        return current;
                      });
                      setUserEscErrors(updatedErrors);
                    }}
                    disabled={isCurrentUserEsc}
                  />
                  <TextInput
                    multiline
                    rows={4}
                    placeholder='Error body'
                    value={error.body || ''}
                    onChange={(e) => {
                      const updatedErrors = userEscErrors.map((current, idx) => {
                        if (idx === i) {
                          return {
                            ...current,
                            body: e.target.value
                          };
                        }
                        return current;
                      });
                      setUserEscErrors(updatedErrors);
                    }}
                    disabled={isCurrentUserEsc}
                  />
                  <div className='user-esc-modal-body-form-error-info'>
                    <Checkbox
                      label='Blocking'
                      checked={error.isBlocking || false}
                      onChange={() => {
                        const updatedErrors = userEscErrors.map((current, idx) => {
                          if (idx === i) {
                            return {
                              ...current,
                              isBlocking: !current.isBlocking
                            };
                          }
                          return current;
                        });
                        setUserEscErrors(updatedErrors);
                      }}
                      disabled={isCurrentUserEsc}
                    />
                    <Checkbox label='Post-submit' checked={error.isPostSubmit || false} disabled />
                    {error.link ? (
                      <span>
                        <span className='user-esc-modal-body-form-error-info-label'>Link:</span>
                        {error.link}
                      </span>
                    ) : null}
                    {error.linkText ? (
                      <span>
                        <span className='user-esc-modal-body-form-error-info-label'>Link text:</span>
                        {error.linkText}
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </GroupBox>
        </ScrollView>
        <Button
          primary
          className='user-esc-modal-body-submit'
          variant='flat'
          size='lg'
          disabled={areEscalationErrorsLoading || isCurrentUserEsc || userEscErrors.every((error) => !error.header)}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </WindowContent>
      <Frame
        variant='status'
        className={classNames('user-esc-modal-footer', { error: footerText.startsWith('Error') })}
      >
        {footerText}
      </Frame>
    </Window>
  );
};

const mapStateToProps = (state) => ({
  user: userSelector(state)
});

const mapDispatchToProps = {
  setModalType
};

export default connect(mapStateToProps, mapDispatchToProps)(UserEscModal);
